import type { JSX } from 'react'

import { AvatarFonts } from "/app/libraries/arcana/src/atoms/Avatar/index.ts"
import { AvatarProfile } from "/app/libraries/arcana/src/atoms/Avatar/Templates/index.ts"
import { Dropdown } from "/app/libraries/arcana/src/blocks/Dropdown/index.ts"
import { DropdownHeaderAccount } from "/app/libraries/arcana/src/blocks/Dropdown/DropdownHeader/Templates/index.ts"
import { DropdownItemAccount, DropdownItemProfile } from "/app/libraries/arcana/src/blocks/Dropdown/DropdownItems/DropdownItemTypeButton/Templates/index.ts"
import { DropdownItemFontColors, DropdownItemFonts, DropdownItemPaddings } from "/app/libraries/arcana/src/blocks/Dropdown/DropdownItems/index.ts"
import { DropdownItemsGroup, DropdownItemsGroupPaddings } from "/app/libraries/arcana/src/blocks/Dropdown/DropdownItemsGroup/index.ts"
import { DropdownItemUsername } from "/app/libraries/arcana/src/blocks/Dropdown/DropdownItems/DropdownItemNoClickable/Templates/index.ts"
import { FIXED_INDENTS } from "/app/libraries/arcana/src/vars/index.ts"
import { Icon, IconNames, IconSizes } from "/app/libraries/arcana/src/atoms/Icon/index.ts"
import { Link } from "/app/libraries/arcana/src/atoms/Link/index.ts"
import { TextBox, TextBoxColors } from "/app/libraries/arcana/src/atoms/TextBox/index.ts"

import { useUnit } from 'effector-react'
import { useTranslation } from 'react-i18next'
import { profilesModel } from '~/entities/profiles'
import { model as subscriber } from '~/entities/subscriber'
import { portalRedirectModel } from '~/features/portal/redirect'
import { profilesFeaturesModel } from '~/features/profiles'
import { model as signInModel } from '~/features/subscriber/sign-in'
import { $libraryModuleLink } from '../model'
import { $actualSelectedModule } from '../select'
import { $navbarDropDownTitles } from '../translations'

NavBarDropdown.displayName = 'NavBarDropdown'

export function NavBarDropdown({
  onInteract,
}: {
  onInteract?: () => void
}): JSX.Element {
  const activeProfile = useUnit(profilesModel.$activeProfile)
  const selectedModule = useUnit($actualSelectedModule)
  const libraryModuleLink = useUnit($libraryModuleLink)
  const profiles = useUnit(profilesModel.$optimisticProfiles)
  const subscriberInfo = useUnit(subscriber.$subscriber)
  const navbarDropDownTitles = useUnit($navbarDropDownTitles)
  const isActiveProfileRegular = useUnit(
    profilesFeaturesModel.$isActiveProfileRegular
  )
  const subscriberAccountSettingsURL = useUnit(
    portalRedirectModel.$subscriberAccountSettingsURL
  )
  const chooseProfile = useUnit(profilesFeaturesModel.handleChooseProfile)
  const signOutFn = useUnit(signInModel.signOut)
  const openAccountSettingsModal = useUnit(
    portalRedirectModel.openAccountSettingsModal
  )
  const { t } = useTranslation()

  return (
    <Dropdown dataTestId='navbar-dropdown' onClickCapture={onInteract}>
      <DropdownHeaderAccount color={TextBoxColors.TEXT_ICON_PRIMARY}>
        <AvatarProfile
          title={activeProfile?.name ?? ''}
          imageUrl={activeProfile?.avatar ?? undefined}
          width={40}
          height={40}
          font={AvatarFonts.FontXsBold}
        />
      </DropdownHeaderAccount>

      <DropdownItemsGroup width={224}>
        <DropdownItemUsername
          title={subscriberInfo?.name ?? ''}
          description={subscriberInfo?.email ?? ''}
          width='224px'
          paddings={DropdownItemPaddings.SIZE_10}
        />

        <DropdownItemsGroup
          width={224}
          paddings={DropdownItemsGroupPaddings.SIZE_30}
          gap={FIXED_INDENTS.INDENT_5}
        >
          {profiles.map((profile) => (
            <DropdownItemProfile
              key={profile.id}
              title={profile.name ?? ''}
              selected={activeProfile?.id === profile.id}
              onClick={() => chooseProfile({ id: profile.id })}
              font={DropdownItemFonts.FontXs}
              paddings={DropdownItemPaddings.SIZE_10}
            >
              <AvatarProfile
                title={profile.name ?? ''}
                imageUrl={profile.avatar ?? undefined}
                width={24}
                height={24}
                font={AvatarFonts.FontXsBold}
                small
              />
              <TextBox title={profile.name ?? ''} maxLines={1} />
              {activeProfile?.id === profile.id && (
                <Icon
                  name={IconNames.selectedFavoriteIcon}
                  size={IconSizes.small}
                />
              )}
            </DropdownItemProfile>
          ))}

          <Link href='/profiles/manage'>
            <DropdownItemAccount
              title={t('profiles_manage')}
              icon={IconNames.editNote}
              selected={selectedModule === 'manage'}
              colorDefault={DropdownItemFontColors.TEXT_ICON_SECONDARY}
              font={DropdownItemFonts.FontXs}
              paddings={DropdownItemPaddings.SIZE_10}
            />
          </Link>
        </DropdownItemsGroup>

        <DropdownItemsGroup
          width={224}
          paddings={DropdownItemsGroupPaddings.SIZE_30}
          gap={FIXED_INDENTS.INDENT_5}
        >
          <Link href={libraryModuleLink}>
            <DropdownItemAccount
              title={navbarDropDownTitles.myList}
              icon={IconNames.bookmark}
              selected={
                selectedModule === 'my_list' || selectedModule === 'library'
              }
              colorDefault={DropdownItemFontColors.TEXT_ICON_SECONDARY}
              font={DropdownItemFonts.FontXs}
            />
          </Link>

          {isActiveProfileRegular && (
            <Link href='/modules/settings'>
              <DropdownItemAccount
                title={navbarDropDownTitles.settings}
                icon={IconNames.settings}
                selected={selectedModule === 'settings'}
                colorDefault={DropdownItemFontColors.TEXT_ICON_SECONDARY}
                font={DropdownItemFonts.FontXs}
              />
            </Link>
          )}

          {subscriberAccountSettingsURL && isActiveProfileRegular && (
            <DropdownItemAccount
              title={navbarDropDownTitles.accountSettings}
              icon={IconNames.accountSettings}
              endIcon={IconNames.redirect}
              dataTestId='navbar-account-btn'
              onClick={() => openAccountSettingsModal()}
              colorDefault={DropdownItemFontColors.TEXT_ICON_SECONDARY}
              font={DropdownItemFonts.FontXs}
            />
          )}

          <DropdownItemAccount
            title={navbarDropDownTitles.logOut}
            icon={IconNames.logOut}
            onClick={() => signOutFn()}
            colorDefault={DropdownItemFontColors.TEXT_ICON_SECONDARY}
            font={DropdownItemFonts.FontXs}
          />
        </DropdownItemsGroup>
      </DropdownItemsGroup>
    </Dropdown>
  )
}
